import { FC } from "react";
import { Flex, Image } from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";

import defaultPictureSmall from "assets/images/profile-pic-placeholder-sm.svg";

export interface MemberAvatarPreview {
  id: number;
  name: string;
  picture: string | null;
}

/**
 * The modern Figma-spec avatar stack (overlapping 28×40 rounded avatars, "+N"
 * overflow chip, optional click-through). Prefer this for new work over the
 * legacy `StackedAvatars`, which keeps the old analytics geometry.
 */
interface MembersAvatarStackProps {
  users: MemberAvatarPreview[];
  total: number;
  onClick?: () => void;
}

const AVATARS_WHEN_OVERFLOW = 4;
const MAX_AVATARS_WITHOUT_OVERFLOW = 5;
const AVATAR_WIDTH = "28px";
const AVATAR_HEIGHT = "40px";
const AVATAR_OVERLAP = "-6px";
const AVATAR_RADIUS = "4px";
const OVERFLOW_CHIP_WIDTH = "32px";

export const MembersAvatarStack: FC<MembersAvatarStackProps> = ({
  users,
  total,
  onClick,
}) => {
  const shouldShowOverflow = total > MAX_AVATARS_WITHOUT_OVERFLOW;
  const visibleCount = shouldShowOverflow
    ? AVATARS_WHEN_OVERFLOW
    : MAX_AVATARS_WITHOUT_OVERFLOW;
  const avatarsToShow = users.slice(0, visibleCount);
  const overflowCount = total - AVATARS_WHEN_OVERFLOW;

  const isClickable = Boolean(onClick);

  return (
    <Flex
      alignItems="center"
      width="fit-content"
      cursor={isClickable ? "pointer" : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? "View template members" : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {avatarsToShow.map((user, idx) => (
        <Image
          key={user.id}
          src={user.picture || defaultPictureSmall}
          alt={user.name}
          width={AVATAR_WIDTH}
          height={AVATAR_HEIGHT}
          minW={AVATAR_WIDTH}
          borderRadius={AVATAR_RADIUS}
          border="2px solid"
          borderColor={theme.palette.brand.primary.white}
          objectFit="cover"
          ml={idx === 0 ? 0 : AVATAR_OVERLAP}
          bg={theme.palette.ui.greys.grey4}
        />
      ))}
      {shouldShowOverflow && (
        <Flex
          width={OVERFLOW_CHIP_WIDTH}
          height={AVATAR_HEIGHT}
          minW={OVERFLOW_CHIP_WIDTH}
          borderRadius={AVATAR_RADIUS}
          border="2px solid"
          borderColor={theme.palette.brand.primary.white}
          bg={theme.palette.ui.greys.grey3}
          ml={AVATAR_OVERLAP}
          alignItems="center"
          justifyContent="center"
          fontFamily="Inter"
          fontSize="12px"
          fontWeight={400}
          color={theme.palette.brand.primary.black}
        >
          +{overflowCount}
        </Flex>
      )}
    </Flex>
  );
};
